import React, { useState } from "react";
import { get, set } from "lodash";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Box from "@amzn/meridian/box";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Icon from "@amzn/meridian/icon";
import Link from "@amzn/meridian/link";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Toggle from "@amzn/meridian/toggle";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import styles from "../style.module.scss";
import { formatInputWithPrefixAndSuffix } from "../../../utils/formattingUtils";
import { renderInput } from "../commons/inputUtility";
import { ImageSheet } from "../commons/ImageSheet";

interface IApproachTableComponentProps {
    isEditing: boolean;
    tableMapping: any;
    data: any;
    handleUpdateData: (data: any) => void;
}

export const ApproachTableComponent: React.FC<IApproachTableComponentProps> = ({
    isEditing,
    tableMapping,
    data,
    handleUpdateData,
}) => {
    const defaultExpandedSections = [
        "propertyCharacteristics",
        "saleInformation",
        "tenetInformation",
        "adjustments",
        "otherAdjustments",
    ];

    const [expandedRows, setExpandedRows] = useState(defaultExpandedSections);
    const [displayImage, setDisplayImage] = useState(() =>
        Array(Object.entries(tableMapping["toggles"]).length + 1).fill(false)
    );

    const updateExpandedRows = (key: string) => {
        setExpandedRows(prev =>
            prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]
        );
    };

    const handleInputChange = (field: string, value: any) => {
        set(data, field, value);
        handleUpdateData(data);
    };

    const handleDisplayImage = (imageStatus: boolean, index?: string | number) => {
        setDisplayImage(prevState => {
            const newState = [...prevState];
            newState[index] = imageStatus;

            return newState;
        });
    };

    const renderImageLink = (cellData: any, columnIndex: number) => (
        <Text
            type={cellData.font || "b300"}
            color={
                get(data, tableMapping["toggles"][columnIndex]) || columnIndex === 0
                    ? "primary"
                    : "secondary"
            }
        >
            <span style={{ textDecoration: "underline" }}>
                <Link onClick={() => handleDisplayImage(true, columnIndex)}>
                    {formatInputWithPrefixAndSuffix(
                        get(data, cellData.key, ""),
                        cellData.prefix,
                        cellData.suffix
                    )}
                </Link>
                <ImageSheet
                    imageHeader={formatInputWithPrefixAndSuffix(
                        get(data, cellData.key, ""),
                        cellData.prefix,
                        cellData.suffix
                    )}
                    s3ObjectIdList={get(data, `${cellData.imageObjectKey}.s3ObjectIdList`, [""])}
                    displayImage={displayImage[columnIndex]}
                    handleDisplayImage={handleDisplayImage}
                    index={columnIndex}
                />
            </span>
        </Text>
    );

    const renderTextContent = (cellData: any, columnIndex: number) => (
        <Text
            type={cellData.font || "b300"}
            color={
                get(data, tableMapping["toggles"][columnIndex]) || columnIndex === 0
                    ? "primary"
                    : "secondary"
            }
        >
            {cellData.key.includes("year") || cellData.key.includes("Year")
                ? get(data, cellData.key, "")
                : formatInputWithPrefixAndSuffix(
                      get(data, cellData.key, ""),
                      cellData.prefix,
                      cellData.suffix
                  )}
        </Text>
    );

    const renderCellContent = (cellData: any, columnIndex: number) => {
        if (
            isEditing &&
            cellData.editable &&
            (get(data, tableMapping["toggles"][columnIndex]) || columnIndex === 0)
        ) {
            return renderInput(cellData, data, handleInputChange);
        }

        if (cellData.type === "ignore") {
            return null;
        }

        if (cellData.imageObjectKey && columnIndex !== 0) {
            return renderImageLink(cellData, columnIndex);
        }

        return renderTextContent(cellData, columnIndex);
    };

    const renderHeaderRow = () => (
        <TableRow key="header">
            <TableCell />
            {tableMapping.headers.map((header: any, index: number) => (
                <TableCell key={index}>
                    {index === 0 ? (
                        header
                    ) : (
                        <Toggle
                            disabled={!isEditing}
                            checked={get(data, tableMapping["toggles"][index])}
                            onChange={() =>
                                handleInputChange(
                                    tableMapping["toggles"][index],
                                    !get(data, tableMapping["toggles"][index])
                                )
                            }
                        >
                            <Text type={"h100"}>{header}</Text>
                        </Toggle>
                    )}
                </TableCell>
            ))}
        </TableRow>
    );

    const renderDataRow = (row: any, sectionKey: string) => (
        <TableRow key={`${sectionKey}-${row.label}`}>
            <TableCell alignmentHorizontal="end">
                <Text type="h100">{row.label}</Text>
            </TableCell>
            {row.cells.map((cell: any[], columnIndex: number) => (
                <TableCell key={columnIndex}>
                    {cell.map(cellData => (
                        <React.Fragment key={cellData.key}>
                            {renderCellContent(cellData, columnIndex)}
                            <Box height={"6px"} />
                        </React.Fragment>
                    ))}
                </TableCell>
            ))}
        </TableRow>
    );

    const renderTableRows = () => {
        const rows = [renderHeaderRow()];

        tableMapping.sections.forEach((section: any) => {
            rows.push(
                <TableRow key={`section-${section.key}`}>
                    <TableCell>
                        <Row>
                            <Button
                                type="icon"
                                size="small"
                                onClick={() => updateExpandedRows(section.key)}
                            >
                                <Icon
                                    tokens={
                                        expandedRows.includes(section.key)
                                            ? chevronDownSmallTokens
                                            : chevronRightSmallTokens
                                    }
                                />
                            </Button>
                            <Text type="h100">{section.title}</Text>
                        </Row>
                    </TableCell>
                    <TableCell width="70%" columnSpan={10} />
                </TableRow>
            );

            if (expandedRows.includes(section.key)) {
                section.rows.forEach((row: any) => {
                    rows.push(renderDataRow(row, section.key));
                });
            }
        });

        return rows;
    };

    return (
        <div className={styles.tableContainer}>
            <Column overflowX={"scroll"}>
                <Table
                    headerRows={1}
                    fixHeaderRows={true}
                    showStripes
                    showDividers
                    spacing={"small"}
                >
                    {renderTableRows()}
                </Table>
            </Column>
        </div>
    );
};
